/**
 * 206 (#285) — remembers that the customer said "Not now" to the push opt-in soft-ask, so it is
 * not put in front of them again for a month. One shared record covers both surfaces (post-payment
 * and rental-active, FR-007) since it is the same yes/no question either way.
 *
 * A distinct key from `installDismissal.ts` (research.md R3) — installing to the Home Screen and
 * enabling push are two independent decisions, and coupling their dismissals would suppress one
 * because the customer declined the other, which is not what either feature's requirements ask
 * for.
 *
 * Same shape as `installDismissal.ts`: a timestamp rather than a boolean (the 30-day window is not
 * expressible without one), per device/browser, never sent anywhere, cleared only when the customer
 * clears their site data. Every read and write is guarded — a throw (Safari private mode, storage
 * disabled) is read as "not dismissed" and the app carries on; failing to remember a dismissal is a
 * far smaller harm than failing to render the app.
 */
export const PUSH_OPT_IN_DISMISSAL_KEY = 'carii:push-opt-in-dismissed-at';

/** 30 days, stated once. */
export const PUSH_OPT_IN_DISMISSAL_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

export function isDismissed(now: number = Date.now()): boolean {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(PUSH_OPT_IN_DISMISSAL_KEY);
  } catch {
    return false;
  }

  if (raw === null || raw === '') return false;

  const dismissedAt = Number(raw);
  if (!Number.isFinite(dismissedAt)) return false;

  // A future timestamp means the device clock moved backwards after the dismissal was written.
  // Treating it as valid would suppress the offer until the clock caught up — indefinitely, for a
  // clock that never does.
  if (dismissedAt > now) return false;

  return now - dismissedAt < PUSH_OPT_IN_DISMISSAL_WINDOW_MS;
}

export function recordDismissal(now: number = Date.now()): void {
  try {
    localStorage.setItem(PUSH_OPT_IN_DISMISSAL_KEY, String(now));
  } catch {
    // Nothing to do and nothing to report: the offer simply reappears on the next visit.
  }
}
